'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useLangCurrency } from '@/contexts/LangCurrencyContext';

interface MobileTabBarProps {
  /** Opens the shared full-nav drawer (rendered by AppLayout). */
  onMoreOpen: () => void;
}

interface TabItem {
  icon: string;
  label: string;
  labelTh: string;
  href: string;
}

/** The four highest-traffic destinations; everything else lives behind More. */
const TAB_ITEMS: TabItem[] = [
  { icon: 'HomeIcon', label: 'Home', labelTh: 'หน้าแรก', href: '/dashboard' },
  { icon: 'SparklesIcon', label: 'Inventory', labelTh: 'คลังเพชร', href: '/diamond-inventory' },
  { icon: 'UserGroupIcon', label: 'CRM', labelTh: 'CRM', href: '/crm' },
  { icon: 'ShoppingCartIcon', label: 'Sales', labelTh: 'ยอดขาย', href: '/sales' },
];

/** Native-style bottom tab bar: 4 destinations + More (opens the full module drawer). */
export default function MobileTabBar({ onMoreOpen }: MobileTabBarProps) {
  const { lang } = useLangCurrency();
  const activePath = usePathname();
  const moreActive = !TAB_ITEMS.some((t) => activePath === t.href);

  return (
    <nav
      aria-label="Primary"
      className="fixed bottom-0 left-0 right-0 z-30 bg-card/95 backdrop-blur-sm border-t border-border/60 pb-[env(safe-area-inset-bottom)]"
    >
      <div className="grid grid-cols-5">
        {TAB_ITEMS.map((item) => {
          const active = activePath === item.href;
          return (
            <Link
              key={item.href}
              href={item.href}
              aria-current={active ? 'page' : undefined}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors ${
                active ? 'text-primary' : 'text-muted-foreground active:bg-surface'
              }`}
            >
              <Icon name={item.icon as never} size={22} strokeWidth={active ? 2.4 : 1.8} />
              <span className="text-[10px] font-medium leading-none">
                {lang === 'EN' ? item.label : item.labelTh}
              </span>
            </Link>
          );
        })}
        <button
          onClick={onMoreOpen}
          aria-haspopup="dialog"
          className={`flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] transition-colors ${
            moreActive ? 'text-primary' : 'text-muted-foreground active:bg-surface'
          }`}
        >
          <Icon name="EllipsisHorizontalIcon" size={22} strokeWidth={moreActive ? 2.4 : 1.8} />
          <span className="text-[10px] font-medium leading-none">
            {lang === 'EN' ? 'More' : 'เพิ่มเติม'}
          </span>
        </button>
      </div>
    </nav>
  );
}
